import React, { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  PortalAuthProvider,
  usePortalAuth,
  formatPortalError,
} from "../../lib/portal-auth-context";
import { LOGO_URL } from "../../lib/i18n";
import SEO from "../../components/SEO";
import { Eye, EyeSlash, ArrowRight, UserCircle } from "@phosphor-icons/react";

const Form = () => {
  const { user, login } = usePortalAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user === undefined) return null;
  if (user) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Bienvenido de vuelta");
      nav("/portal", { replace: true });
    } catch (err) {
      setError(
        formatPortalError(err.response?.data?.detail) || err.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[var(--icor-navy)] text-white flex flex-col items-center justify-center px-6 relative"
      data-testid="portal-login-page"
    >
      <SEO title="Portal Cliente · ICOR" noIndex />
      <div className="absolute inset-0 bg-grid opacity-25" aria-hidden />
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(55% 60% at 50% 0%, rgba(0,82,255,0.22), transparent 60%)",
        }}
        aria-hidden
      />

      <a href="/" className="relative mb-8">
        <img
          src={LOGO_URL}
          alt="ICOR"
          className="h-20 w-auto"
          style={{
            mixBlendMode: "screen",
            filter: "brightness(2.6) contrast(1.1)",
          }}
        />
      </a>

      <div
        className="relative w-full max-w-md bg-white text-slate-900 p-8 md:p-10 border border-white/10"
        style={{ borderRadius: 4 }}
      >
        <div className="flex items-center gap-2 text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)]">
          <UserCircle size={14} weight="bold" />
          Portal Cliente
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
          Ingresa a tu cuenta
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Consulta tus pedidos, facturas y catálogo personalizado.
        </p>

        <form onSubmit={submit} className="mt-8" data-testid="portal-login-form">
          <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
            Correo
          </label>
          <input
            type="email"
            required
            data-testid="portal-login-email"
            className="icor-input mb-5"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@institucion.com"
            autoFocus
          />

          <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
            Contraseña
          </label>
          <div className="relative mb-5">
            <input
              type={showPass ? "text" : "password"}
              required
              data-testid="portal-login-password"
              className="icor-input pr-12"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPass((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label="toggle"
            >
              {showPass ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && (
            <div
              data-testid="portal-login-error"
              className="mb-5 p-3 text-sm text-red-700 bg-red-50 border border-red-200"
              style={{ borderRadius: 4 }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="portal-login-submit"
            className="btn-primary w-full disabled:opacity-60"
          >
            {submitting ? "Entrando..." : "Iniciar sesión"}
            <ArrowRight size={16} />
          </button>

          <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600 text-center">
            ¿Primera vez aquí?{" "}
            <Link
              to="/portal/register"
              className="text-[var(--icor-blue)] font-medium hover:underline"
              data-testid="portal-login-to-register"
            >
              Crea tu cuenta
            </Link>
          </div>
        </form>
      </div>

      <a
        href="/"
        className="relative mt-6 text-xs tracking-[0.18em] uppercase text-white/50 hover:text-white"
      >
        ← Volver al sitio público
      </a>
    </div>
  );
};

export default function PortalLogin() {
  return (
    <PortalAuthProvider>
      <Form />
      <Toaster position="top-right" richColors />
    </PortalAuthProvider>
  );
}
